import React, {useContext, useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {useAuth0} from '@auth0/auth0-react'
import {useNavigate, useLocation} from 'react-router'
import {Tooltip} from '../../core/shared/Tooltip'
import {selectedLinkType} from '../../app-store/kpGlobalStateSlice'
import {headerContext} from '../../context/header-context'
import {useUserFeatures} from '../../context/user-features-context'
import {useWindowDimensions} from '../../utils/use-window-dimensions'
import {getEnvironment} from '../../utils/get-environment'
import {DataDogEndSession} from '../../dataDogConfig'
import {
  selectEffectiveRole,
  selectEnvConfig,
  selectUserData,
  selectUserRoles,
} from '../../app-store/selector'
import {UserRoleSwitcher} from '../header-components/UserRoleSwitcher'
import {
  Avatar,
  HeaderBar,
  HeaderPill,
  IconButton,
  LogoutIcon,
  HamburgerButton,
  HeaderTitleBlock,
  HeaderTitle,
  HeaderSubtitle,
  PrimaryAction,
  UserButton,
  UserInfo,
  UserFullName,
  // UserRole,
  UserMenu,
  MenuProfile,
  MenuProfileText,
  MenuProfileName,
  MenuProfileEmail,
  MenuEmailRow,
  CopyButton,
  DetailsCard,
  DetailRow,
  DetailLabel,
  DetailValue,
  RoleChips,
  RoleChip,
} from './AppHeader.styles'

const MOBILE_BREAKPOINT = 835

function CopyIcon({copied}) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      {copied ? (
        <path
          d="m5 12.5 4.5 4.5L19 7.5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <>
          <rect
            x="9"
            y="9"
            width="11"
            height="11"
            rx="2.2"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M5 15V6.2A2.2 2.2 0 0 1 7.2 4H16"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </>
      )}
    </svg>
  )
}

function HamburgerIcon({open}) {
  // Three rounded bars (short middle bar); they cross into an X when open.
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <line
        x1="3"
        y1="4.5"
        x2="15"
        y2="4.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        style={{transform: open ? 'translateY(4.5px) rotate(45deg)' : 'none'}}
      />
      <line
        x1="3"
        y1="9"
        x2="11"
        y2="9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        style={{opacity: open ? 0 : 1}}
      />
      <line
        x1="3"
        y1="13.5"
        x2="15"
        y2="13.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        style={{transform: open ? 'translateY(-4.5px) rotate(-45deg)' : 'none'}}
      />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M6 1.5v9M1.5 6h9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function getNameParts(user = {}) {
  if (user?.given_name || user?.family_name) {
    return {first: user.given_name || '', last: user.family_name || ''}
  }
  const [first = '', ...rest] = (user?.name || user?.nickname || '')
    .trim()
    .split(/\s+/)
  return {first, last: rest.join(' ')}
}

function useRouteAction() {
  const {Orders_Menu_Access: hasOrdersLinkAccess} = useUserFeatures()
  const {pathname} = useLocation()
  const routeButtonData = {
    orders: {
      route: '/order/new',
      text: 'New Purchase Link',
      condition: hasOrdersLinkAccess,
    },
    'demo-requests': {
      route: '/new-demo-request',
      text: 'New Demo',
      condition: true,
    },
    coupons: {
      route: '/coupons/new',
      text: 'New Coupon',
      condition: true,
    },
    'proposal-tool': {
      // Stay on the current proposal route and ask ProposalHeader to open
      // its creation modal (see its openNewProposal effect).
      route: pathname,
      options: {state: {openNewProposal: true}},
      text: 'New Proposal',
      condition: true,
    },
  }
  const info = routeButtonData[pathname.split('/')[1]]
  return info?.condition ? info : null
}

// Page headings shown at the left of the bar. First match wins; pages should
// not render their own heading for a route listed here.
const routeTitleData = [
  {
    prefix: '/accounts',
    title: 'Account Management',
    subtitle: 'Search accounts, view hierarchy, and manage orders',
  },
  {prefix: '/proposal-tool', title: 'Proposal Tool'},
]

// Keap Partner 1:Many catalog (the 1:1 flow shares this route but has no
// header title; `?legacy=1` keeps the old page, which renders its own heading).
const keapManyTitle = {
  title: '1:Many Purchase Link',
  subtitle: 'Build a reusable purchase link for multiple customers',
}

function useRouteTitle() {
  const {pathname, search} = useLocation()
  const linkType = useSelector(selectedLinkType)
  if (
    pathname.startsWith('/keap-partner-catalog') &&
    linkType === '1:many' &&
    new URLSearchParams(search).get('legacy') !== '1'
  ) {
    return keapManyTitle
  }
  return routeTitleData.find(({prefix}) => pathname.startsWith(prefix)) || null
}

// Shared by the core OOE shell and the Keap Partner shell.
//  - `actions`: [{key, text, onClick, plain?}] — overrides the per-route action
//  - `logo`: node rendered on the left (Keap Partner has no sidebar)
//  - `fullWidth`: no sidebar to clear, so the bar starts at the left edge
function AppHeader({
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
  isSidebarOpen,
  actions,
  logo,
  fullWidth = false,
  blurOnScroll = false,
}) {
  const {isHeaderVisible} = useContext(headerContext)
  const navigate = useNavigate()
  const {logout} = useAuth0()
  const {width: windowWidth} = useWindowDimensions()
  const {user} = useSelector(selectUserData)
  const {isSwitchRoleUser} = useSelector(selectUserRoles)
  const userRoles = useSelector(selectEffectiveRole)
  const envConfig = useSelector(selectEnvConfig)
  const routeAction = useRouteAction()
  const routeTitle = useRouteTitle()
  const buttons =
    actions ||
    (routeAction
      ? [
          {
            key: 'route-action',
            text: routeAction.text,
            onClick: () => navigate(routeAction.route, routeAction.options),
          },
        ]
      : [])
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isEmailCopied, setIsEmailCopied] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const pillRef = useRef(null)
  const isMobile = windowWidth < MOBILE_BREAKPOINT
  const {first, last} = getNameParts(user)
  const initials = `${first[0] || ''}${last[0] || ''}`.toUpperCase()

  // <main> is the scroller (not the window). scroll doesn't bubble, so listen
  // in the capture phase on document.
  useEffect(() => {
    if (!blurOnScroll) {
      setIsScrolled(false)
      return undefined
    }
    const handleScroll = e => {
      if (e.target?.tagName === 'MAIN') setIsScrolled(e.target.scrollTop > 0)
    }
    document.addEventListener('scroll', handleScroll, true)
    return () => document.removeEventListener('scroll', handleScroll, true)
  }, [blurOnScroll])

  useEffect(() => {
    const handleClickOutside = e => {
      if (pillRef.current && !pillRef.current.contains(e.target)) {
        setIsMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!isHeaderVisible) {
    return null
  }

  const handleCopyEmail = async () => {
    if (!user?.email) return
    try {
      await navigator.clipboard.writeText(user.email)
      setIsEmailCopied(true)
      setTimeout(() => setIsEmailCopied(false), 1500)
    } catch (e) {
      // clipboard unavailable (insecure context / denied) — nothing to confirm
    }
  }

  const handleLogout = () => {
    DataDogEndSession()
    logout({returnTo: window.location.origin})
  }

  return (
    <HeaderBar
      data-theme="ooe-v1"
      isSidebarOpen={isSidebarOpen}
      fullWidth={fullWidth}
      isScrolled={isScrolled}
    >
      {logo}
      {routeTitle && (
        <HeaderTitleBlock>
          <HeaderTitle>{routeTitle.title}</HeaderTitle>
          {routeTitle.subtitle && (
            <HeaderSubtitle>{routeTitle.subtitle}</HeaderSubtitle>
          )}
        </HeaderTitleBlock>
      )}
      <HeaderPill ref={pillRef} hasActions={buttons.length > 0}>
        {isMobile && setIsMobileSidebarOpen && (
          <HamburgerButton
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={isMobileSidebarOpen}
            onClick={() => setIsMobileSidebarOpen(prev => !prev)}
          >
            <HamburgerIcon open={isMobileSidebarOpen} />
          </HamburgerButton>
        )}
        {buttons.map(({key, text, onClick, plain}) => (
          <PrimaryAction
            key={key || text}
            type="button"
            plain={plain}
            onClick={onClick}
          >
            {!plain && (
              <span className="plus">
                <PlusIcon />
              </span>
            )}
            {text}
          </PrimaryAction>
        ))}
        <UserButton
          type="button"
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(prev => !prev)}
        >
          <UserInfo>
            <UserFullName>{`${first} ${last}`.trim()}</UserFullName>
            {/* {userRoles?.[0] && <UserRole>{userRoles[0]}</UserRole>} */}
          </UserInfo>
          <Avatar>
            <span>{initials}</span>
          </Avatar>
        </UserButton>
        <IconButton type="button" aria-label="Sign out" onClick={handleLogout}>
          <LogoutIcon aria-hidden />
        </IconButton>

        {/* Always mounted (hidden when closed): UserRoleSwitcher keeps the
            external role store in sync, which getUserRoles reads app-wide. */}
        <UserMenu open={isMenuOpen}>
          <MenuProfile>
            <Avatar size={42}>
              <span>{initials}</span>
            </Avatar>
            <MenuProfileText>
              <MenuProfileName>{`${first} ${last}`.trim()}</MenuProfileName>
              <MenuEmailRow>
                <MenuProfileEmail title={user?.email}>
                  {user?.email}
                </MenuProfileEmail>
                <Tooltip
                  size="sm"
                  variant="top"
                  description={isEmailCopied ? 'Copied' : 'Copy email'}
                >
                  <CopyButton
                    type="button"
                    copied={isEmailCopied}
                    aria-label={isEmailCopied ? 'Email copied' : 'Copy email'}
                    onClick={handleCopyEmail}
                  >
                    <CopyIcon copied={isEmailCopied} />
                  </CopyButton>
                </Tooltip>
              </MenuEmailRow>
            </MenuProfileText>
          </MenuProfile>
          <DetailsCard>
            <DetailRow>
              <DetailLabel>Role</DetailLabel>
              <RoleChips>
                {userRoles?.map(role => (
                  <RoleChip key={role}>{role}</RoleChip>
                ))}
              </RoleChips>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Environment</DetailLabel>
              <DetailValue>{getEnvironment(envConfig?.hostname)}</DetailValue>
            </DetailRow>
            <DetailRow>
              {isSwitchRoleUser && <UserRoleSwitcher variant="redesign" />}
            </DetailRow>
          </DetailsCard>
        </UserMenu>
      </HeaderPill>
    </HeaderBar>
  )
}

export {AppHeader}
